/**
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
 */

import { mapGetters, mapActions } from 'vuex';
import languageMixin from '@/mixins/language';
import { getUserByRoomNumber, getFreeMeetingCredentials } from '@/api/meeting';
import 'url-search-params-polyfill';
export default {
  name: 'FreeClient',
  mixins: [languageMixin],
  components: {},
  props: {
    room: {
      type: [String, Number],
      default: null
    },
    dname: {
      type: String,
      default: null
    },
    exitUrl: {
      type: String,
      default: null
    },
    user: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      displayName: null,
      onSettingsPage: false,
      roomNumber: null,
      roomOwner: null,
      roomPinPopup: false,
      roomPin: null,
      verifyingRoomPin: false,
      savedSettings: null
    };
  },
  computed: {
    ...mapGetters('AppStore', {
      $_CONFIG: 'config',
      bandwidth: 'bandwidth',
      freeclientName: 'freeclientName',
      freeclientRoom: 'freeclientRoom',
      isDesktopApp: 'isDesktopApp',
      selectedIODevices: 'selectedIODevices'
    }),
    urlToExit () {
      return !this.exitUrl ? `${this.$_CONFIG.baseurl}freeclient` : this.exitUrl;
    }
  },
  async created () {
    this.roomNumber = this.room;
    this.displayName = this.dname;
    if (this.roomNumber) {
      if (this.user) {
        this.roomOwner = this.user;
      } else {
        this.roomOwner = await this.fetchUserByRoomNumber(this.roomNumber);
      }
      if (!this.roomOwner || !this.roomOwner.room) {
        this.roomOwner = {
          room: this.roomNumber,
          fname: ''
        };
      }
    }
    this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
        case 'AppStore/setIsDesktopApp':
          this.desktopAppSetup(state.isDesktopApp);
          break;
      }
    });
  },
  async mounted () {
    if (!this.roomNumber) {
      this.roomNumber = this.freeclientRoom;
    }
    if (!this.displayName) {
      this.displayName = this.freeclientName;
    }
    this.desktopAppSetup(this.isDesktopApp);
    this.$eventBus.$emit('appReady');
  },
  beforeDestroy () {},
  methods: {
    ...mapActions('AppStore', {
      setFreeClientInfo: 'setFreeClientInfo',
      setSelectedIODevices: 'setSelectedIODevices'
    }),
    desktopAppSetup (status) {},
    onGoBack () {
      if (this.onSettingsPage) {
        this.onSettingsPage = false;
      } else {
        this.$router.replace({ name: 'desktop', params: { signin: true } });
      }
    },
    clearDisplayName () {
      this.displayName = null;
    },
    clearRoom () {
      this.roomNumber = null;
    },
    clearRoomPin () {
      this.roomPin = null;
    },
    async fetchUserByRoomNumber (room) {
      return getUserByRoomNumber(room);
    },
    async gotoMeeting (settings) {
      if (!settings) {
        settings = this.savedSettings;
      } else {
        this.savedSettings = settings;
      }
      localStorage.setItem('SETTINGS_STORE', JSON.stringify({
        audioDestination: settings.audioOutput ? settings.audioOutput.deviceId : null,
        audioSource: settings.audioInput ? settings.audioInput.deviceId : null,
        videoSource: settings.videoInput ? settings.videoInput.deviceId : null
      }));
      await this.onSettingsChange(settings);
      if (this.$refs.meetingSubmitForm) {
        if (!this.roomNumber || this.roomNumber.trim().length < 2) {
          this.$eventBus.$emit('showToast', {
            iconLeft: 'SvgClose',
            text: this.$t('APP_TEXT_ENTER_THE_ROOM'),
            static: false,
            short: true
          });
          return;
        }
        if (!this.displayName || this.displayName.trim().length < 3) {
          this.$eventBus.$emit('showToast', {
            iconLeft: 'SvgClose',
            text: this.$t('APP_TEXT_ENTER_YOUR_NAME'),
            static: false,
            short: true
          });
          return;
        }
        await this.setFreeClientInfo({ room: this.roomNumber, name: this.displayName });
        const meetData = {
          room: this.roomNumber,
          dname: this.displayName,
          bandwidth: this.bandwidth,
          authorized: false,
          protocol: 'webrtc'
        };
        if (this.roomPin) {
          meetData.pin = this.roomPin;
        }
        this.verifyingRoomPin = true;
        const credential = await getFreeMeetingCredentials(meetData);
        if (credential === 401) {
          this.showRoomPinPopup();
          return false;
        } else if (credential && credential.token) {
          this.$refs.meetingSubmitForm.submitToMeeting();
          this.verifyingRoomPin = false;
        }
      }
    },
    hideRoomPinPopup () {
      if (this.verifyingRoomPin) {
        return;
      }
      this.roomPinPopup = false;
      this.roomPin = null;
    },
    showSettingsPage () {
      if (!this.roomNumber || this.roomNumber.trim().length < 2) {
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgClose',
          text: this.$t('APP_TEXT_ENTER_THE_ROOM'),
          static: false,
          short: true
        });
        return;
      }
      if (!this.displayName || this.displayName.trim().length < 3) {
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgClose',
          text: this.$t('APP_TEXT_ENTER_YOUR_NAME'),
          static: false,
          short: true
        });
        return;
      }
      this.onSettingsPage = true;
    },
    onBandWidthChange (newValue) {},
    async onSettingsChange ({
      audioInput,
      audioOutput,
      videoInput,
      remember
    }) {
      if (remember) {
        this.setSelectedIODevices({
          audioInput: audioInput ? audioInput.deviceId : null,
          audioOutput: audioOutput ? audioOutput.deviceId : null,
          videoInput: videoInput ? videoInput.deviceId : null,
          remember
        });
      } else {
        this.setSelectedIODevices({
          audioInput: null,
          audioOutput: null,
          videoInput: null,
          remember
        });
      }
    },
    openTermsOfService () {
      if (this.isDesktopApp) {
        this.$_NATIVE.openInBrowser(this.$_CONFIG.appconfig.TERMS_OF_SERVICE_URL);
      } else {
        window.open(this.$_CONFIG.appconfig.TERMS_OF_SERVICE_URL, '_blank');
      }
    },
    resetLoginFrame () {
      this.$refs.maskedLoginForm.reset();
    },
    showRoomPinPopup () {
      if (this.roomPinPopup) {
        this.$eventBus.$emit('showToast', {
          iconLeft: 'SvgClose',
          text: this.$t('APP_TEXT_ROOM_PIN_VERIFICATION_FAILED'),
          static: false,
          short: true
        });
      }
      this.roomPin = null;
      this.verifyingRoomPin = false;
      this.roomPinPopup = true;
      const self = this;
      setTimeout(() => {
        if (self.$refs.roomPinTextBox) {
          self.$refs.roomPinTextBox.inputFocusIn();
        }
      }, 100);
    }
  }
};
